import React, { useState } from 'react';
import { motion } from 'framer-motion';
import QuestionnaireForm from './QuestionnaireForm';

export default function Hero() {
  return (
    <section className="relative overflow-hidden" id="hero">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_eliteroofing/artifacts/bdauq1pi_2024-01-19-10-07-41-170%202.mov" type="video/mp4" />
        </video>
        
        {/* Light Frosted Overlay - 15% opacity to let video shine through */}
        <div className="absolute inset-0 bg-white/15"></div>
      </div>

      {/* Background Pattern (kept subtle) */}
      <div className="absolute inset-0 opacity-3 z-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%231e3a5f" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-20 mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Column - Content */}
          <motion.div 
            className="space-y-6 sm:space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}
          >
            <div className="space-y-4">
              <div className="inline-block">
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[hsl(var(--accent))] text-white text-xs sm:text-sm font-bold uppercase tracking-wide rounded-full">
                  Transparency Builds Trust!
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight tracking-tight text-slate-900">
                Professional Exterior Solutions Delivered with Speed & Quality
              </h1>
              
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg border border-slate-200">
                <p className="text-base sm:text-lg md:text-xl text-slate-700 leading-relaxed">
                  Elite Roofing specializes in professional roof restoration after wind & hail storms for both residential & commercial properties. <strong className="text-slate-900">Schedule a FREE Roof Inspection</strong> and get your roof assessed by Elite Roofing today.
                </p>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-700">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-700">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Photo Documentation</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-700">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">HOA & Commercial Ready</span>
              </div>
            </div>

            {/* President Section - Hidden on mobile, shown on tablet+ */}
            <div className="hidden md:block bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-[hsl(var(--primary))]">
                    <img 
                      src="/assets/jeremy.jpg" 
                      alt="Jeremy Felix - President" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">Jeremy Felix</h3>
                  <p className="text-base text-slate-600 mb-4">President of Elite Roofing & Construction</p>
                  <p className="text-base text-slate-700 italic leading-relaxed">
                    "We look forward to meeting you for your FREE Roof Inspection! Submit your info to get a call from one of our roofing experts to schedule your inspection today."
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile President Section - Compact version */}
            <div className="md:hidden bg-white rounded-xl p-4 shadow-lg border border-slate-200">
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-[hsl(var(--primary))]">
                    <img 
                      src="/assets/jeremy.jpg" 
                      alt="Jeremy Felix - President" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Jeremy Felix</h3>
                  <p className="text-sm text-slate-600">President of Elite Roofing & Construction</p>
                </div>
              </div>
              <p className="text-sm text-slate-700 italic leading-relaxed">
                "We look forward to meeting you for your FREE Roof Inspection! Submit your info to get a call from one of our roofing experts to schedule your inspection today."
              </p>
            </div>
          </motion.div>

          {/* Right Column - Lead Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <QuestionnaireForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}